import { AccredibleResponsiveMenuItem } from '@accredible-frontend-v2/components/responsive-menu';
import { AccredibleUser } from '@accredible-frontend-v2/models';
import { AccredibleAccountsRedirectionService } from '@accredible-frontend-v2/services/accounts-redirection';
import { AccredibleAuthenticationApiService } from '@accredible-frontend-v2/services/authentication';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleUserApiService } from '@accredible-frontend-v2/services/user';
import { AccredibleToastService } from '@accredible-frontend-v2/toast';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

const SIGN_IN = 'signIn';
const BTN_SIGN_IN = 'btn-sign-in';
const SPOTLIGHT = 'spotlight';

enum TranslationKey {
  SIGN_IN = 'sl-header-menu.sign_in',
  SIGN_OUT = 'sl-header-menu.sign_out',
  LOGOUT_FAILED = 'sl-header-menu.logout_failed',
  HELP = 'sl-header-menu.help',
  HELP_CENTER = 'language.help_links.help_center',
  ACCOUNT_SETTINGS = 'sl-header-menu.account_settings',
}

enum MenuKey {
  HELP = 'rmi-help',
  SIGN_OUT = 'rmi-signOut',
  ACCOUNT_SETTINGS = 'rmi-accountsSettings',
}

@UntilDestroy()
@Component({
  selector: 'sl-header-menu',
  templateUrl: './header-menu.component.html',
  styleUrls: [`./header-menu.component.scss`],
  // ChangeDetectionStrategy.OnPush can't be activated (in both this and accredible-base-header-menu components) due to an issue in the MaterialMenu Component.
  // If activated the attribute aria-expanded="true" is not removed
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderMenuComponent implements OnInit {
  @Input()
  extraDynamicMenuItems: AccredibleResponsiveMenuItem[];
  @Input()
  enableDirectoriesLink = false;
  @Input()
  signInTextOverride: string;
  @Input()
  openLoginInNewWindow = false;
  @Input()
  noSignIn = false;

  readonly user$: Observable<AccredibleUser> = this._userApi.user$;
  readonly languageCode$ = this._language.languageCode$;

  dynamicMenuItems: AccredibleResponsiveMenuItem[] = [];
  staticMenuItems: AccredibleResponsiveMenuItem[] = [];

  private _loginMenuItem: AccredibleResponsiveMenuItem;

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _authenticationApi: AccredibleAuthenticationApiService,
    private readonly _userApi: AccredibleUserApiService,
    private readonly _accountsRedirection: AccredibleAccountsRedirectionService,
    private readonly _language: AccredibleLanguageService,
    private readonly _toast: AccredibleToastService,
  ) {}

  ngOnInit(): void {
    this._loginMenuItem = {
      id: SIGN_IN,
      label: this.signInTextOverride || this._language.selectTranslate(TranslationKey.SIGN_IN),
      actionBtn: this._onLogin.bind(this),
      divider: true,
      itemClass: BTN_SIGN_IN, // used for button specific styling
    };

    this._setupLanguageCodeSubscription();
  }

  onMenuActivated(): void {
    this._setMenuItems();
  }

  private _setupLanguageCodeSubscription(): void {
    // We need to update menu items when language changes in order to update the language query param of account settings menu button
    this.languageCode$.pipe(untilDestroyed(this)).subscribe(() => {
      this._setMenuItems();
    });
  }

  private _onLogin(): void {
    // TODO(Gathercole): When the user is on the 404 page this should get the origin query param from this._document.location.origin rather than this._document.location.href
    const loginUrl = this._accountsRedirection.getLoginUrlHref(SPOTLIGHT);
    if (this.openLoginInNewWindow) {
      // Open login in new window, some customers prefer to open login in a new window
      window.open(loginUrl, '_blank');
    } else {
      // Default behaviour
      this._document.location.href = loginUrl;
    }
  }

  private _onLogout(): void {
    this._authenticationApi.logout().subscribe({
      next: () => {
        this.dynamicMenuItems = this.noSignIn ? [] : [this._loginMenuItem];
        this.staticMenuItems = [];
      },
      error: () => {
        this._toast.error(this._language.translate(TranslationKey.LOGOUT_FAILED));
      },
    });
  }

  private _setMenuItems(): void {
    const currentUser = this._userApi.getUser();
    if (currentUser) {
      this.staticMenuItems = [
        {
          id: MenuKey.HELP,
          label: this._language.selectTranslate(TranslationKey.HELP),
          externalLink: this._language.selectTranslate(TranslationKey.HELP_CENTER),
        },
        {
          id: MenuKey.SIGN_OUT,
          divider: true,
          label: this._language.selectTranslate(TranslationKey.SIGN_OUT),
          actionBtn: this._onLogout.bind(this),
        },
      ];

      if (!currentUser.restrict_to_single_issuer) {
        this.staticMenuItems.unshift({
          id: MenuKey.ACCOUNT_SETTINGS,
          label: this._language.selectTranslate(TranslationKey.ACCOUNT_SETTINGS),
          externalLink: this._accountsRedirection.getDirectoryProfilesUrl('spotlight'),
        });
      }

      this.dynamicMenuItems = [];
    }

    if (!currentUser) {
      this.dynamicMenuItems = this.noSignIn ? [] : [this._loginMenuItem];
    }

    // If we have extra menu items we add them here
    if (this.extraDynamicMenuItems) {
      this.dynamicMenuItems = [...this.extraDynamicMenuItems, ...this.dynamicMenuItems];
    }
  }
}
